import { Controller } from "@hotwired/stimulus"
import { apiHeaders, debounce, formatDate, notifySessionUpdated, patchJson, responseErrorMessage, setSaveStatus, writeFetch } from "./sweeper_shared"

export default class extends Controller {
  static values = {
    users: Array,
    metadataUrl: String,
    virtuesUrl: String,
    failureModesUrl: String,
    countermeasuresUrl: String,
    verdictUrl: String
  }

  connect() {
    this.sessions = []
    this.currentId = this.content?.dataset.sessionId || ""
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    this.onSessionUpdated = this.handleSessionUpdated.bind(this)
    this.onEditorActivated = this.handleEditorActivated.bind(this)
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("sweeper:session-updated", this.onSessionUpdated)
    document.addEventListener("sweeper:editor-activated", this.onEditorActivated)
    this.sessionsLoaded = this.loadSessions().then(() => this.renderBar())
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("sweeper:session-updated", this.onSessionUpdated)
    document.removeEventListener("sweeper:editor-activated", this.onEditorActivated)
  }

  select(event) {
    this.currentId = event.target.value || ""
    this.renderEditor()
    this.notifySessionSelected()
  }

  updateName(event) {
    const session = this.currentSession
    if (!session) return

    session.name = event.target.value
    this.renderOptions()
    debounce(this, "metadata:name", () => {
      this.saveMetadata({ name: event.target.value }).then(() => this.reloadMatrix())
    })
  }

  async destroy() {
    const session = this.currentSession
    if (!session) return
    if (!confirm("Weet je zeker dat je deze sessie wilt verwijderen? Dit kan niet ongedaan gemaakt worden.")) return

    const response = await writeFetch(`/api/v1/sessions/${encodeURIComponent(session.id)}`, {
      method: "DELETE",
      credentials: "include",
      headers: apiHeaders()
    })
    if (!response.ok) {
      setSaveStatus(await responseErrorMessage(response, "Verwijderen mislukt"), false)
      return
    }

    this.sessions = this.sessions.filter(existing => existing.id !== session.id)
    document.dispatchEvent(new CustomEvent("sweeper:session-deleted", { detail: { id: session.id } }))
    this.currentId = ""
    this.renderBar()
    this.renderEditor()
    this.reloadMatrix()
    document.querySelector('[data-tab="matrix"]')?.click()
  }

  toggleHosts(event) {
    const removeId = event.target.dataset.removeHost
    const session = this.currentSession
    if (removeId && session) {
      session.hostUserIds = (session.hostUserIds || []).filter(id => String(id) !== removeId)
      this.renderHostPicker()
      this.saveMetadata({ hostUserIds: session.hostUserIds })
      return
    }

    this.hostPopover.classList.toggle("hidden")
    this.hostSearch.focus()
    this.renderHostPicker()
  }

  searchHosts() {
    this.renderHostPicker()
  }

  chooseHost(event) {
    const option = event.target.closest(".host-option")
    const session = this.currentSession
    if (!option || !session) return

    const userId = Number(option.dataset.userId)
    const ids = new Set(session.hostUserIds || [])
    ids.has(userId) ? ids.delete(userId) : ids.add(userId)
    session.hostUserIds = Array.from(ids)
    this.renderHostPicker()
    this.saveMetadata({ hostUserIds: session.hostUserIds })
  }

  toggleBlock(event) {
    const head = event.target.closest(".block-head")
    if (!head || !this.element.contains(head)) return

    head.parentElement.classList.toggle("collapsed")
  }

  handleDocumentClick(event) {
    if (event.target.closest("#btn-new-session")) {
      event.preventDefault()
      this.createSession()
      return
    }

    if (!this.element.contains(event.target) && !this.hostPopover.classList.contains("hidden")) {
      this.hostPopover.classList.add("hidden")
    }
  }

  handleSessionUpdated(event) {
    const session = event.detail?.session
    if (!session?.id) return

    const index = this.sessions.findIndex(existing => existing.id === session.id)
    if (index >= 0) this.sessions[index] = session
    else this.sessions.push(session)
    if (session.id === this.currentId) this.renderBar()
  }

  async handleEditorActivated() {
    await this.sessionsLoaded
    const params = new URLSearchParams(window.location.search)
    const pathSession = window.location.pathname.match(/^\/sweeper\/editor\/([^/]+)$/)?.[1]
    if (pathSession) this.currentId = decodeURIComponent(pathSession)
    else if (params.get("session")) this.currentId = params.get("session")
    this.renderBar()
    this.renderEditor({ skipIfLoaded: true })
  }

  async createSession() {
    const response = await writeFetch("/api/v1/sessions", {
      method: "POST",
      credentials: "include",
      headers: apiHeaders(),
      body: JSON.stringify({
        name: "Nieuwe sessie",
        format: "breakout",
        verdict: { ready: null, notes: "" }
      })
    })
    if (!response.ok) {
      setSaveStatus(await responseErrorMessage(response, "Aanmaken mislukt"), false)
      return
    }

    const session = await response.json()
    this.currentId = session.id
    notifySessionUpdated(session)
    this.renderBar()
    this.renderEditor()
    this.reloadMatrix()
    this.notifySessionSelected()
    if (!window.location.pathname.match(/^\/sweeper\/editor\//)) {
      window.location.assign(`/sweeper/editor/${encodeURIComponent(this.currentId)}`)
    }
  }

  async loadSessions() {
    const response = await fetch("/api/v1/sessions", {
      credentials: "include",
      headers: apiHeaders()
    })
    if (!response.ok) return

    this.sessions = await response.json()
  }

  saveMetadata(attrs) {
    if (!this.currentId) return Promise.resolve()

    return patchJson(`/api/v1/sessions/${encodeURIComponent(this.currentId)}/metadata`, attrs)
  }

  renderBar() {
    this.renderOptions()
    const session = this.currentSession
    this.nameInput.value = session?.name || ""
    this.lastSavedElements.forEach(element => {
      element.textContent = session?.updatedAt ? `Laatst opgeslagen ${formatDate(session.updatedAt)}` : "-"
    })
    this.renderHostPicker()
  }

  renderOptions() {
    const sorted = [...this.sessions].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    this.selectInput.innerHTML = '<option value="">- kies sessie -</option>' +
      sorted.map(session => {
        const selected = session.id === this.currentId ? "selected" : ""
        return `<option value="${this.escapeAttr(session.id)}" ${selected}>${this.escapeHtml(session.name || "(naamloos)")}</option>`
      }).join("")
  }

  renderEditor(options = {}) {
    if (!this.currentSession) {
      this.content.dataset.sessionId = ""
      this.content.innerHTML = '<div class="empty-state">Kies of maak een sessie om te beginnen.</div>'
      this.renderBar()
      this.renderDangerZone()
      return
    }

    if (options.skipIfLoaded && this.content.dataset.sessionId === this.currentId) {
      this.renderDangerZone()
      return
    }

    this.content.dataset.sessionId = this.currentId
    this.content.innerHTML = [
      this.frameHtml("editor-metadata", this.metadataUrlValue, "Sessie-info wordt geladen..."),
      this.frameHtml("editor-virtues", this.virtuesUrlValue, "Must-haves worden geladen..."),
      this.frameHtml("editor-failure-modes", this.failureModesUrlValue, "Failure modes worden geladen..."),
      this.frameHtml("editor-countermeasures", this.countermeasuresUrlValue, "Countermeasures worden geladen..."),
      this.frameHtml("editor-verdict", this.verdictUrlValue, "Oordeel wordt geladen...")
    ].join("")
    this.renderBar()
    this.renderDangerZone()
  }

  frameHtml(id, url, text) {
    return `<turbo-frame id="${id}" src="${url}?session=${encodeURIComponent(this.currentId)}">
      <div class="block"><div class="block-body"><div class="empty-state">${text}</div></div></div>
    </turbo-frame>`
  }

  renderHostPicker() {
    const session = this.currentSession
    const selected = new Set(((session && session.hostUserIds) || []).map(id => String(id)))
    const query = (this.hostSearch.value || "").trim().toLowerCase()
    const users = this.usersValue.filter(user => user.label.toLowerCase().includes(query))

    this.hostOptions.innerHTML = users.length
      ? users.map(user => `
        <button type="button" class="host-option ${selected.has(String(user.id)) ? "selected" : ""}" data-user-id="${user.id}">
          <span>${this.escapeHtml(user.label)}</span>
          <span class="check">${selected.has(String(user.id)) ? "✓" : ""}</span>
        </button>
      `).join("")
      : '<div class="host-placeholder" style="padding:8px 4px">Geen users</div>'

    const names = session ? this.hostNamesFromIds(session.hostUserIds) : []
    const extraCount = Math.max(names.length - 1, 0)
    this.hostTrigger.innerHTML = names.length
      ? `
        <span class="host-chip">
          <span class="host-chip-label">${this.escapeHtml(names[0])}</span>
          <span class="host-chip-remove" data-remove-host="${session.hostUserIds[0]}" aria-label="Remove host">×</span>
        </span>
        ${extraCount ? `<span class="host-chip more">+${extraCount}</span>` : ""}
      `
      : '<span class="host-placeholder">Host</span>'
  }

  renderDangerZone() {
    this.dangerZone?.classList.toggle("hidden", !this.currentSession)
  }

  hostNamesFromIds(ids) {
    const selected = new Set((ids || []).map(id => String(id)))
    return this.usersValue.filter(user => selected.has(String(user.id))).map(user => user.label)
  }

  reloadMatrix() {
    const frame = document.getElementById("view-matrix")
    if (!frame) return

    const url = "/sweeper/matrix"
    if (frame.getAttribute("src") === url && typeof frame.reload === "function") frame.reload()
    else frame.setAttribute("src", url)
  }

  notifySessionSelected() {
    document.dispatchEvent(new CustomEvent("sweeper:editor-session-selected", {
      detail: { sessionId: this.currentId }
    }))
  }

  escapeHtml(value) {
    return String(value || "").replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char])
  }

  escapeAttr(value) {
    return this.escapeHtml(value)
  }

  get currentSession() {
    return this.sessions.find(session => session.id === this.currentId)
  }

  get selectInput() { return document.getElementById("session-select") }
  get nameInput() { return document.getElementById("session-name") }
  get content() { return document.getElementById("editor-content") }
  get lastSavedElements() { return document.querySelectorAll("[data-last-saved]") }
  get hostTrigger() { return document.getElementById("session-hosts-trigger") }
  get hostPopover() { return document.getElementById("session-hosts-popover") }
  get hostSearch() { return document.getElementById("session-hosts-search") }
  get hostOptions() { return document.getElementById("session-hosts-options") }
  get dangerZone() { return document.getElementById("session-danger-zone") }
}
